/* eslint-disable no-use-before-define */
import {useEffect, useRef} from "react"
import {LOAD_MORE, PRODUCT_LOAD_TRIGGER} from "../../models/PageLoadTrigger"

interface InitialScrollTopHookOptions {
    requestedPage: number
    gridContainerElement: HTMLElement | null
    productLoadTrigger: PRODUCT_LOAD_TRIGGER
    onWillRepositionScroll?: VoidFunction
    onAfterScrollReposition?: VoidFunction
    fetchNextPage: () => Promise<any>
    setInitialScrollCompleted: (boolean) => void
    shouldScrollPage?: boolean
}

/**
 * Sets the initial scrollbar position on the initial page load
 */
export function useInitialScrollPosition({
    requestedPage,
    gridContainerElement,
    onWillRepositionScroll,
    onAfterScrollReposition,
    fetchNextPage,
    setInitialScrollCompleted,
    productLoadTrigger,
    shouldScrollPage = false,
}: InitialScrollTopHookOptions): void {
    const hasExecuted = useRef(false)

    useEffect(() => {
        if (helpers.shouldBeRepositioned(requestedPage)) {
            if (onWillRepositionScroll) onWillRepositionScroll()
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        helpers.disableAutomaticScrollRestoration()
        window.addEventListener("beforeunload", helpers.enableAutomaticScrollRestoration)

        return () => {
            helpers.enableAutomaticScrollRestoration()
            window.removeEventListener("beforeunload", helpers.enableAutomaticScrollRestoration)
        }
    }, [])

    useEffect(() => {
        /* eslint-disable @typescript-eslint/no-floating-promises */
        ;(async function asyncUseEffect() {
            if (hasExecuted.current || !gridContainerElement) {
                return
            }

            hasExecuted.current = true

            const urlSpecifiedTop = helpers.getUrlSpecifiedScrollTop() || 0
            if (window.scrollY !== urlSpecifiedTop) {
                window.scrollTo(0, urlSpecifiedTop)
            }
            const restoreScrollPosition = () => {
                window.scrollTo(0, urlSpecifiedTop)
                setInitialScrollCompleted(true)
                if (onAfterScrollReposition) onAfterScrollReposition()
            }
            if (urlSpecifiedTop || shouldScrollPage) {
                switch (productLoadTrigger) {
                    case LOAD_MORE: {
                        if (window.scrollY < urlSpecifiedTop) {
                            await fetchNextPage().then(() => {
                                restoreScrollPosition()
                            })
                        } else {
                            restoreScrollPosition()
                        }
                        return
                    }
                    default:
                        await fetchNextPage().then(() => {
                            restoreScrollPosition()
                        })
                        break
                }
            } else {
                if (requestedPage >= 2) {
                    // this allows the page to be scrolled up to load the previous page
                    window.scrollTo(0, 1)
                }
                setInitialScrollCompleted(true)
            }
        })()
    }, [
        gridContainerElement,
        requestedPage,
        productLoadTrigger,
        fetchNextPage,
        setInitialScrollCompleted,
        onAfterScrollReposition,
        shouldScrollPage,
    ])
}

function shouldBeRepositioned(requestedPage: number) {
    return requestedPage >= 2 || helpers.getUrlSpecifiedScrollTop()
}

export function getUrlSpecifiedScrollTop(): number | null {
    const scrollTop = Number(window.location.hash.replace("#", ""))
    if (!scrollTop || Number.isNaN(scrollTop)) return null
    return scrollTop
}

function disableAutomaticScrollRestoration() {
    if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = "manual"
    }
}

function enableAutomaticScrollRestoration() {
    if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = "auto"
    }
}

// These helpers have been setup like this to allow
// them to be spied / mocked out in the test
export const helpers = {
    shouldBeRepositioned,
    getUrlSpecifiedScrollTop,
    enableAutomaticScrollRestoration,
    disableAutomaticScrollRestoration,
}
